import { AlertTriangle, CloudOff, CloudUpload, GitMerge } from "lucide-react";
import { STATUS_LABEL, PRIORITY_LABEL } from "@desk/contracts";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { useConnection, isOffline } from "@/data/connection";
import { outbox, useOutbox } from "@/data/outbox";
import type { OutboxEntry, OutboxOp } from "@/data/outbox-core";
import { cn, plural } from "@/lib/utils";

export function describeOp(op: OutboxOp): string {
  switch (op.type) {
    case "task.create":
      return `Create “${op.input.title}”`;
    case "task.update":
      return `Edit ${Object.keys(op.patch).filter((k) => k !== "baseRevision").join(", ")}`;
    case "task.action":
      return op.action[0]!.toUpperCase() + op.action.slice(1);
    case "task.snooze":
      return "Snooze";
    case "task.move":
      return "Move";
    case "plan.add":
      return "Add to Today";
    case "plan.remove":
      return "Remove from Today";
    default:
      return "Checklist change";
  }
}

/** Global sync indicator: Live / Offline / N pending / N failed. */
export function SyncStatus() {
  const conn = useConnection();
  const { entries } = useOutbox();
  const pending = entries.filter((e) => e.state === "pending" || e.state === "sending");
  const failed = entries.filter((e) => e.state === "failed" || e.state === "conflict");
  const offline = isOffline(conn) || conn.live === "offline";

  let icon = null;
  let label = "";
  let tone = "text-subtle";
  if (failed.length) {
    icon = <AlertTriangle className="size-3.5" />;
    label = `${failed.length} failed — retry`;
    tone = "text-destructive";
  } else if (pending.length) {
    icon = <CloudUpload className="size-3.5" />;
    label = `${plural(pending.length, "change")} pending sync`;
  } else if (offline) {
    icon = <CloudOff className="size-3.5" />;
    label = "Offline";
  }

  if (!icon) {
    return (
      <span className="inline-flex h-7 items-center gap-1.5 px-2 text-[12px] text-subtle" title={conn.live === "live" ? "Live updates on" : "Connecting…"}>
        <span className={cn("size-1.5 rounded-full", conn.live === "live" ? "bg-success" : "bg-subtle/60")} aria-hidden />
        {conn.live === "live" ? "Synced" : "Connecting…"}
      </span>
    );
  }
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={cn("inline-flex h-7 max-w-full items-center gap-1.5 rounded-md px-2 text-[12px] hover:bg-sidebar-accent", tone)}>
          {icon}
          <span className="truncate">{label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" className="w-[300px] p-2">
        <p className="px-1 pb-2 text-[12.5px] text-muted-foreground">
          {offline ? "Desk can't reach the service. Changes are saved on this device and sync automatically." : "Changes waiting to sync."}
        </p>
        <ul className="flex max-h-60 flex-col gap-0.5 overflow-y-auto">
          {[...failed, ...pending].map((e: OutboxEntry) => (
            <li key={e.id} className="flex items-center gap-2 rounded-md px-1 py-1 text-[13px]">
              <span className="min-w-0 flex-1">
                <span className="block truncate">{describeOp(e.op)}</span>
                {e.state !== "pending" && e.state !== "sending" && <span className="block truncate text-[12px] text-destructive">{e.error}</span>}
              </span>
              {(e.state === "failed" || e.state === "conflict") && (
                <>
                  {e.state === "failed" && (
                    <Button size="xs" variant="outline" onClick={() => void outbox.retry(e.id)}>
                      Retry
                    </Button>
                  )}
                  <Button size="xs" variant="quiet" onClick={() => void outbox.discard(e.id)}>
                    Discard
                  </Button>
                </>
              )}
            </li>
          ))}
        </ul>
        {pending.length > 0 && (
          <Button size="sm" variant="ghost" className="mt-1 w-full" onClick={() => outbox.retryAll()}>
            Try now
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}

function fmt(field: string, v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (field === "status") return STATUS_LABEL[v as keyof typeof STATUS_LABEL] ?? String(v);
  if (field === "priority") return PRIORITY_LABEL[v as keyof typeof PRIORITY_LABEL] ?? String(v);
  if (Array.isArray(v)) return v.join(", ") || "—";
  return String(v);
}

const FIELD_LABEL: Record<string, string> = { title: "Title", description: "Notes", deadline: "Deadline", labels: "Labels", priority: "Priority", status: "Status", reminderAt: "Reminder" };

/** 409 conflict: show their values next to the user's, keep the user's text, let them choose. */
export function ConflictDialog() {
  const { entries } = useOutbox();
  const entry = entries.find((e) => e.state === "conflict");
  if (!entry) return null;
  const current = (entry.current ?? {}) as Record<string, unknown>;
  const mine: Record<string, unknown> =
    entry.op.type === "task.update"
      ? Object.fromEntries(Object.entries(entry.op.patch).filter(([k]) => k !== "baseRevision"))
      : entry.op.type === "task.move"
        ? { ...entry.op.dest }
        : {};
  const fields = Object.keys(mine);
  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent hideClose className="max-w-[560px]" onEscapeKeyDown={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogBody>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="size-4 text-muted-foreground" />
            {String(current.readableId ?? "Task")} changed somewhere else
          </DialogTitle>
          <DialogDescription>Your edit hasn't been saved yet. Choose which version to keep.</DialogDescription>
          <div className="grid grid-cols-[96px_1fr_1fr] gap-x-3 gap-y-2 text-[13px]">
            <span />
            <span className="font-medium text-muted-foreground">Theirs (saved)</span>
            <span className="font-medium text-muted-foreground">Yours</span>
            {fields.map((f) => (
              <div key={f} className="contents">
                <span className="pt-1 text-muted-foreground">{FIELD_LABEL[f] ?? f}</span>
                <div className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded-md bg-muted px-2 py-1">{fmt(f, current[f])}</div>
                <div className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded-md border border-foreground/20 px-2 py-1">{fmt(f, mine[f])}</div>
              </div>
            ))}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => void outbox.discard(entry.id)}>
            Keep theirs
          </Button>
          <Button variant="default" onClick={() => void outbox.useMine(entry.id)}>
            Use mine (re-apply on latest)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
